import { useEffect, useState, useCallback } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Edit2, Archive, X, Loader2, CheckCircle2, Tag, DollarSign } from "lucide-react";

const formatAud = (cents) => `AU$${(cents / 100).toFixed(2)}`;
const INTERVALS = [
  { v: "month", n: 1, label: "Monthly" },
  { v: "month", n: 3, label: "Every 3 months" },
  { v: "month", n: 6, label: "Every 6 months" },
  { v: "year", n: 1, label: "Yearly" },
];

const AdminPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("plans");
  const [showPlanForm, setShowPlanForm] = useState(null);   // null | {} | plan
  const [showCouponForm, setShowCouponForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/admin/plans"),
        api.get("/admin/coupons"),
      ]);
      setPlans(p.plans || []);
      setCoupons(c.coupons || []);
    } catch { toast.error("Failed to load billing config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const archivePlan = async (plan) => {
    if (!window.confirm(`Archive "${plan.name}"? Existing subscribers keep access; new signups will not see it.`)) return;
    try {
      await api.del?.(`/admin/plans/${plan.id}`) ?? (await import("axios")).default.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/plans/${plan.id}`, { withCredentials: true }
      );
      toast.success("Plan archived");
      load();
    } catch { toast.error("Archive failed"); }
  };

  const toggleCoupon = async (c, active) => {
    try {
      await api.put(`/admin/coupons/${c.id}?active=${active}`, {});
      toast.success(active ? "Coupon active" : "Coupon paused");
      load();
    } catch { toast.error("Update failed"); }
  };

  const toggleBanner = async (c, show) => {
    try {
      await api.patch(`/admin/coupons/${c.id}/banner?show=${show}`, {});
      toast.success(show ? "Banner promotion on" : "Banner promotion off");
      load();
    } catch { toast.error("Update failed"); }
  };

  const deleteCoupon = async (c) => {
    if (!window.confirm(`Permanently delete coupon ${c.code}?`)) return;
    try {
      const axiosLib = (await import("axios")).default;
      await axiosLib.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/coupons/${c.id}`, { withCredentials: true });
      toast.success("Coupon deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-plans-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Billing & coupons</h1>
          <p className="text-sm text-muted-foreground">Manage subscription plans + promo codes. Changes sync to Stripe automatically.</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-border">
        {[
          { k: "plans", icon: DollarSign, label: `Plans (${plans.length})` },
          { k: "coupons", icon: Tag, label: `Coupons (${coupons.length})` },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            data-testid={`admin-billing-tab-${t.k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-1.5 ${tab === t.k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : tab === "plans" ? (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setShowPlanForm({})} data-testid="admin-plans-new" className="rounded-full">
              <Plus className="w-4 h-4 mr-1" /> New plan
            </Button>
          </div>
          {plans.length === 0 ? (
            <EmptyState msg="No plans yet. Create your first plan." />
          ) : plans.map((p) => (
            <div key={p.id} data-testid={`admin-plan-row-${p.id}`} className="rounded-xl border border-border p-4 bg-card flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{p.name}</span>
                  {!p.active && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Archived</span>}
                  {p.trial_days > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30">{p.trial_days}d trial</span>}
                </div>
                {p.description && <div className="text-xs text-muted-foreground mt-1">{p.description}</div>}
                <div className="text-xs text-muted-foreground mt-1">
                  <strong className="text-foreground">{formatAud(p.amount_cents)}</strong>
                  {p.one_time ? " · one-time payment" : ` · every ${p.interval_count > 1 ? `${p.interval_count} ` : ""}${p.interval}`}
                  {p.plan_key && <span className="ml-2 font-mono text-[10px] px-1.5 py-0.5 rounded bg-secondary">{p.plan_key}</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowPlanForm(p)} data-testid={`admin-plan-edit-${p.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs border border-border hover:bg-secondary inline-flex items-center gap-1">
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
                {p.active && <button onClick={() => archivePlan(p)} data-testid={`admin-plan-archive-${p.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs border border-destructive/30 text-destructive hover:bg-destructive/5 inline-flex items-center gap-1">
                  <Archive className="w-3 h-3" /> Archive
                </button>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setShowCouponForm(true)} data-testid="admin-coupons-new" className="rounded-full">
              <Plus className="w-4 h-4 mr-1" /> New coupon
            </Button>
          </div>
          {coupons.length === 0 ? (
            <EmptyState msg="No coupons yet." />
          ) : coupons.map((c) => {
            const s = c.stats || { redemptions: 0, revenue_cents: 0 };
            return (
            <div key={c.id} data-testid={`admin-coupon-row-${c.id}`} className="rounded-xl border border-border p-4 bg-card flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="font-mono font-bold bg-secondary px-2 py-0.5 rounded text-sm">{c.code}</code>
                  <span className="text-xs text-muted-foreground">
                    {c.percent_off ? `${c.percent_off}% off` : `${formatAud(c.amount_off_cents)} off`}
                    {" · "}{c.duration === "once" ? "first payment" : c.duration === "forever" ? "forever" : `${c.duration_in_months} months`}
                  </span>
                  {!c.active && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">Paused</span>}
                  {c.active && c.show_on_banner && <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30">On site banner</span>}
                </div>
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1" data-testid={`admin-coupon-stats-${c.id}`}>
                  <span><strong className="text-foreground">{s.redemptions}</strong> {s.redemptions === 1 ? "redemption" : "redemptions"}</span>
                  <span>· <strong className="text-foreground">{formatAud(s.revenue_cents || 0)}</strong> revenue</span>
                  {s.discount_cents > 0 && <span>· {formatAud(s.discount_cents)} discounted</span>}
                  {s.last_at && <span>· last {new Date(s.last_at).toLocaleDateString()}</span>}
                </div>
                {c.max_redemptions && <div className="text-xs text-muted-foreground mt-1">Max redemptions: {c.max_redemptions}</div>}
                {c.redeem_by_iso && <div className="text-xs text-muted-foreground mt-1">Expires: {new Date(c.redeem_by_iso).toLocaleString()}</div>}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => toggleBanner(c, !c.show_on_banner)} data-testid={`admin-coupon-banner-${c.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs border border-border hover:bg-secondary">
                  {c.show_on_banner ? "Hide banner" : "Show banner"}
                </button>
                <button onClick={() => toggleCoupon(c, !c.active)} data-testid={`admin-coupon-toggle-${c.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs border border-border hover:bg-secondary">
                  {c.active ? "Pause" : "Resume"}
                </button>
                <button onClick={() => deleteCoupon(c)} data-testid={`admin-coupon-delete-${c.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs border border-destructive/30 text-destructive hover:bg-destructive/5">Delete</button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {showPlanForm !== null && (
        <PlanForm plan={showPlanForm.id ? showPlanForm : null} onClose={() => setShowPlanForm(null)} onSaved={() => { setShowPlanForm(null); load(); }} />
      )}
      {showCouponForm && (
        <CouponForm onClose={() => setShowCouponForm(false)} onSaved={() => { setShowCouponForm(false); load(); }} />
      )}
    </div>
  );
};

const EmptyState = ({ msg }) => (
  <div className="text-center py-12 text-muted-foreground text-sm border border-dashed border-border rounded-xl">{msg}</div>
);

const PlanForm = ({ plan, onClose, onSaved }) => {
  const isEdit = !!plan;
  const [name, setName] = useState(plan?.name || "");
  const [description, setDescription] = useState(plan?.description || "");
  const [amountAud, setAmountAud] = useState(plan ? plan.amount_cents / 100 : "");
  const [planType, setPlanType] = useState(plan?.one_time ? "one_time" : "recurring");
  const [planKey, setPlanKey] = useState(plan?.plan_key || "");
  const [intervalKey, setIntervalKey] = useState(plan ? `${plan.interval}-${plan.interval_count}` : "month-1");
  const [trialDays, setTrialDays] = useState(plan?.trial_days || 0);
  const [active, setActive] = useState(plan?.active !== false);
  const [saving, setSaving] = useState(false);
  const isOneTime = planType === "one_time";

  const submit = async (e) => {
    e.preventDefault();
    if (!amountAud) return;
    const [interval, interval_count] = intervalKey.split("-");
    const payload = {
      name, description: description || null,
      amount_cents: Math.round(parseFloat(amountAud) * 100),
      active,
    };
    if (!isEdit) {
      // one_time + plan_key are only settable on create (PlanUpdate ignores them)
      payload.one_time = isOneTime;
      payload.plan_key = planKey.trim() || null;
    }
    if (isOneTime) {
      payload.trial_days = 0;
    } else {
      payload.interval = interval;
      payload.interval_count = parseInt(interval_count, 10);
      payload.trial_days = parseInt(trialDays || 0, 10);
    }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/admin/plans/${plan.id}`, payload);
      else await api.post("/admin/plans", payload);
      toast.success(isEdit ? "Plan updated" : "Plan created · Stripe synced");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? "Edit plan" : "New plan"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="admin-plan-form">
        <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} required data-testid="plan-name" className="form-input" placeholder="PassADC Pro — Monthly" /></Field>
        <Field label="Description (optional)"><input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="plan-desc" className="form-input" placeholder="Full access, billed monthly" /></Field>
        <Field label="Plan type">
          <select value={planType} onChange={(e) => setPlanType(e.target.value)} disabled={isEdit} data-testid="plan-type" className="form-input disabled:opacity-60">
            <option value="recurring">Recurring subscription</option>
            <option value="one_time">One-time payment (e.g. March Pass)</option>
          </select>
          {isEdit && <span className="block text-[11px] text-muted-foreground mt-1">Plan type can't be changed after creation.</span>}
        </Field>
        {!isEdit && (
          <Field label="Plan key (optional — stable id for code, e.g. march_pass_2027)">
            <input value={planKey} onChange={(e) => setPlanKey(e.target.value.trim())} data-testid="plan-key" className="form-input font-mono" placeholder="march_pass_2027" />
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Amount (AUD)"><input type="number" step="0.01" min="1" value={amountAud} onChange={(e) => setAmountAud(e.target.value)} required data-testid="plan-amount" className="form-input" placeholder={isOneTime ? "89.00" : "49.00"} /></Field>
          {!isOneTime && (
            <Field label="Billing interval">
              <select value={intervalKey} onChange={(e) => setIntervalKey(e.target.value)} data-testid="plan-interval" className="form-input">
                {INTERVALS.map((i) => <option key={`${i.v}-${i.n}`} value={`${i.v}-${i.n}`}>{i.label}</option>)}
              </select>
            </Field>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {!isOneTime && <Field label="Trial days (0 = none)"><input type="number" min="0" max="90" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} data-testid="plan-trial" className="form-input" /></Field>}
          <Field label="Active"><label className="flex items-center gap-2 pt-2 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="plan-active" /> Visible to students</label></Field>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="plan-submit" className="rounded-full">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Save</Button>
        </div>
      </form>
    </Modal>
  );
};

const CouponForm = ({ onClose, onSaved }) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [discType, setDiscType] = useState("percent");
  const [percent, setPercent] = useState("20");
  const [amount, setAmount] = useState("");
  const [duration, setDuration] = useState("once");
  const [months, setMonths] = useState(3);
  const [max, setMax] = useState("");
  const [redeemBy, setRedeemBy] = useState("");
  const [showBanner, setShowBanner] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      code: code.toUpperCase(),
      name: name || null,
      duration,
      duration_in_months: duration === "repeating" ? parseInt(months, 10) : null,
      max_redemptions: max ? parseInt(max, 10) : null,
      redeem_by_iso: redeemBy ? new Date(redeemBy).toISOString() : null,
      active: true,
      show_on_banner: showBanner,
    };
    if (discType === "percent") payload.percent_off = parseFloat(percent);
    else { payload.amount_off_cents = Math.round(parseFloat(amount) * 100); payload.currency = "aud"; }
    setSaving(true);
    try {
      await api.post("/admin/coupons", payload);
      toast.success("Coupon created · Stripe synced");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New coupon" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="admin-coupon-form">
        <Field label="Code (uppercase)"><input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required pattern="[A-Z0-9_\-]+" data-testid="coupon-code" className="form-input font-mono" placeholder="FLASH30" /></Field>
        <Field label="Banner headline (shown on site if promoted)"><input value={name} onChange={(e) => setName(e.target.value)} data-testid="coupon-name" className="form-input" placeholder="Flash sale · this weekend only" /></Field>
        <Field label="Discount type">
          <div className="flex gap-2">
            <label className="flex items-center gap-1 text-sm"><input type="radio" checked={discType === "percent"} onChange={() => setDiscType("percent")} /> Percentage</label>
            <label className="flex items-center gap-1 text-sm"><input type="radio" checked={discType === "amount"} onChange={() => setDiscType("amount")} /> Fixed AUD</label>
          </div>
        </Field>
        {discType === "percent" ? (
          <Field label="Percent off (1-100)"><input type="number" min="1" max="100" value={percent} onChange={(e) => setPercent(e.target.value)} required data-testid="coupon-percent" className="form-input" /></Field>
        ) : (
          <Field label="Amount off (AUD)"><input type="number" step="0.01" min="0.5" value={amount} onChange={(e) => setAmount(e.target.value)} required data-testid="coupon-amount" className="form-input" /></Field>
        )}
        <Field label="Duration">
          <select value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="coupon-duration" className="form-input">
            <option value="once">First payment only</option>
            <option value="forever">Every payment</option>
            <option value="repeating">Repeating (X months)</option>
          </select>
        </Field>
        {duration === "repeating" && <Field label="For how many months"><input type="number" min="1" max="24" value={months} onChange={(e) => setMonths(e.target.value)} data-testid="coupon-months" className="form-input" /></Field>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Max total redemptions (blank = unlimited)"><input type="number" min="1" value={max} onChange={(e) => setMax(e.target.value)} data-testid="coupon-max" className="form-input" /></Field>
          <Field label="Expires at (optional, drives countdown)"><input type="datetime-local" value={redeemBy} onChange={(e) => setRedeemBy(e.target.value)} data-testid="coupon-redeem-by" className="form-input" /></Field>
        </div>
        <Field label="Site banner">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showBanner} onChange={(e) => setShowBanner(e.target.checked)} data-testid="coupon-show-banner" />
            Auto-promote on the public site banner while active
          </label>
        </Field>
        <div className="flex gap-2 justify-end pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="coupon-submit" className="rounded-full">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}Create</Button>
        </div>
      </form>
    </Modal>
  );
};

const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-card rounded-2xl border border-border shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h2 className="font-semibold">{title}</h2>
        <button onClick={onClose} className="p-1 hover:bg-secondary rounded"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-4">{children}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{label}</span>
    {children}
  </label>
);

export default AdminPlansPage;
